import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import { AuthProvider } from './auth/AuthContext';
import { ToastProvider } from './components/primitives';
import { queryClient } from './lib/queryClient';
import { LoginRoute } from './routes/login';
import { SignupRoute } from './routes/signup';
import { ProtectedLayout } from './routes/protected-layout';
import { OpsRoute } from './routes/ops';
// N2 (2026-05-08) — /inventory 단일 페이지 + tab state 영역 영역. 옛 sub-route 영역 영역 폐기.
import { InventoryLayout } from './routes/inventory';
import { ScheduleLayout } from './routes/schedule';
import { ScheduleOverviewRoute } from './routes/schedule/overview';
import { ScheduleCommonRoute } from './routes/schedule/common';
import { ScheduleMorningRoute } from './routes/schedule/morning';
import { ScheduleAfternoonRoute } from './routes/schedule/afternoon';
import { SchedulePrepRoute } from './routes/schedule/prep';
import { ScheduleClosingRoute } from './routes/schedule/closing';
import { SettingsLayout } from './routes/settings';
import { SettingsStoresRoute } from './routes/settings/stores';
import { SettingsRecipesLayout } from './routes/settings/recipes';
import { SettingsRecipesCostRoute } from './routes/settings/recipes/cost';
import { SettingsRecipesMenusRoute } from './routes/settings/recipes/menus';
import { SettingsSuppliersRoute } from './routes/settings/suppliers';
import { SettingsStaffRoute } from './routes/settings/staff';
import { SettingsIntegrationsRoute } from './routes/settings/integrations';
import { SettingsCollectRoute } from './routes/settings/collect';
import { SettingsWindowProgramRoute } from './routes/settings/window-program';
import { ProfileRoute } from './routes/profile';

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginRoute />,
  },
  // Phase 2.5 — 사장 자가 가입 폼 (MVP). 운영자 승인 전까지 미인증 상태 유지.
  {
    path: '/signup',
    element: <SignupRoute />,
  },
  {
    path: '/__collect-preview',
    element: <SettingsCollectRoute />,
  },
  // Phase 6.B-4 — 옛 /app-mini/* 진입을 새 BoxHero 패널로 redirect.
  // dvvb-api onRequest hook 에서도 처리하지만, NPM 라우팅이 web-app 컨테이너로
  // 직접 보내는 경우를 대비한 SPA 차원 fallback. (replace 로 history pollution 방지.)
  { path: '/app-mini', element: <Navigate to="/settings/integrations" replace /> },
  { path: '/app-mini/*', element: <Navigate to="/settings/integrations" replace /> },
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      { index: true, element: <Navigate to="/ops" replace /> },
      { path: 'ops', element: <OpsRoute /> },
      // 2026-05-03 Track A 잔여 — TopNav 프로필 dropdown 항목의 라우트.
      // /me 응답 (= AuthContext.me) 을 read-only 카드로 표시.
      { path: 'profile', element: <ProfileRoute /> },
      // N2 (2026-05-08) — 단일 페이지 + tab state. 옛 sub-route (/low /auto) 폐기.
      { path: 'inventory', element: <InventoryLayout /> },
      // 옛 path (예: /inventory/low /inventory/auto) → /inventory redirect.
      { path: 'inventory/*', element: <Navigate to="/inventory" replace /> },
      {
        path: 'schedule',
        element: <ScheduleLayout />,
        children: [
          { index: true, element: <ScheduleOverviewRoute /> },
          { path: 'common', element: <ScheduleCommonRoute /> },
          { path: 'morning', element: <ScheduleMorningRoute /> },
          { path: 'afternoon', element: <ScheduleAfternoonRoute /> },
          { path: 'prep', element: <SchedulePrepRoute /> },
          { path: 'closing', element: <ScheduleClosingRoute /> },
        ],
      },
      {
        path: 'settings',
        element: <SettingsLayout />,
        children: [
          { index: true, element: <Navigate to="/settings/stores" replace /> },
          // Phase 6.B-3-redo — 매장분류는 같은 페이지 안에서 platform 탭 + store 선택을
          // state 로 관리. nested /stores/:id 라우트 제거.
          { path: 'stores', element: <SettingsStoresRoute /> },
          // Phase 6.D-9 — 레시피 sub-route 2 (원가계산·메뉴).
          // 메뉴추가/레시피추가 sub 통합 (사용자 신고 1번). add 는 menus 로 redirect (legacy).
          {
            path: 'recipes',
            element: <SettingsRecipesLayout />,
            children: [
              { index: true, element: <Navigate to="/settings/recipes/menus" replace /> },
              { path: 'cost', element: <SettingsRecipesCostRoute /> },
              { path: 'menus', element: <SettingsRecipesMenusRoute /> },
              { path: 'add', element: <Navigate to="/settings/recipes/menus" replace /> },
            ],
          },
          { path: 'collect', element: <SettingsCollectRoute /> },
          { path: 'download', element: <SettingsWindowProgramRoute /> },
          // 옛 path /settings/window-program 진입 시 /settings/download 로 흡수.
          // (nav id 가 'download' 라서 path 도 /download 로 유지. 다른 명세 도착 시 변경.)
          { path: 'window-program', element: <Navigate to="/settings/download" replace /> },
          { path: 'suppliers', element: <SettingsSuppliersRoute /> },
          { path: 'staff', element: <SettingsStaffRoute /> },
          { path: 'integrations', element: <SettingsIntegrationsRoute /> },
          // Phase 6.B-3-redo hotfix — settings 하위 옛 nested paths
          // (예: /settings/stores/:storeId/menus, /settings/integrations) 진입 시
          // settings index 로 redirect. 북마크/히스토리 fallback.
          { path: '*', element: <Navigate to="/settings/stores" replace /> },
        ],
      },
    ],
  },
  // Phase 6.B-3-redo hotfix — 최상위 catch-all wildcard. 알 수 없는 path 는
  // 운영 탭으로 redirect (이전엔 NotFoundRoute, 사용자 신고 후 SPA 친화 fallback 으로 변경).
  { path: '*', element: <Navigate to="/ops" replace /> },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
